import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ALL_CITIES, STAFF_ACTIONS, STAFF_MODULES, type StaffAction, type StaffModule } from "@ichegou/contracts";
import { Link } from "@tanstack/react-router";
import { api, call } from "../lib/api.ts";
import { auditText } from "../lib/audit.ts";
import { ago, fmtDateTime, fmtInt } from "../lib/format.ts";
import { useCan, useSession } from "../lib/session.tsx";
import { Icon } from "../components/Icon.tsx";
import { PageHead } from "../components/Shell.tsx";
import { ConfirmDialog, Dialog, ErrorBox, Skeleton, Stat, useToast } from "../components/ui.tsx";

const MODULES: Record<StaffModule, [string, string]> = {
  corridas: ["Corridas", "Lista, detalhes, reembolsos"],
  motoristas: ["Motoristas", "Cadastros, documentos, suspensão"],
  passageiros: ["Passageiros", "Perfis e bloqueios"],
  financeiro: ["Financeiro", "Receita e conciliação"],
  repasses: ["Repasses Pix", "Lotes e reprocessamento"],
  seguranca: ["Segurança", "Ocorrências (SOS e relatos), gravações e alertas de rota"],
  tarifas: ["Tarifas e zonas", "Preços e dinâmica"],
  equipe: ["Equipe", "Membros e funções"],
  suporte: ["Suporte", "Atendimento e reembolsos"],
};
const ACTIONS: Record<string, string> = { ver: "Ver", editar: "Editar", aprovar: "Aprovar", exportar: "Exportar" };
const ROLE_COLOR: Record<string, string> = { administrador: "var(--ink)", operacoes: "#FF6B1A", suporte: "#2563EB", financeiro: "#1F9D55", cadastros: "#B7791F" };
const STATUS = { active: ["Ativo", "tone-ok"], invited: ["Convite pendente", "tone-warn"], inactive: ["Desativado", "tone-neutral"] } as const;

const teamQuery = { queryKey: ["team"], queryFn: () => call(api.admin.team.get()) };
type Team = Awaited<ReturnType<typeof teamQuery.queryFn>>;
type Member = Team["members"][number];

export function TeamPage() {
  const me = useSession();
  const canEdit = useCan("equipe", "editar");
  const team = useQuery(teamQuery);
  const audit = useQuery({ queryKey: ["audit"], queryFn: () => call(api.admin.team.audit.get({ query: { limit: 12 } })), refetchInterval: 30_000 });
  const [q, setQ] = useState("");
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<Member | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const toggle = useMutation({
    mutationFn: (m: Member) => call(api.admin.team({ userId: m.userId })({ action: m.status === "inactive" ? "reactivate" : "deactivate" }).post()),
    onSuccess: (r) => {
      toast.show(r.active ? "Acesso reativado" : "Acesso desativado");
      void qc.invalidateQueries({ queryKey: ["team"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
  });

  if (team.isPending) return <><PageHead icon="key" title="Equipe e permissões" /><Skeleton h={300} /></>;
  if (team.error || !team.data) return <><PageHead icon="key" title="Equipe e permissões" /><ErrorBox error={team.error} /></>;
  const t = team.data;
  const roleName = new Map(t.roles.map((r) => [r.id, r.name]));
  const cityName = new Map(t.cities.map((c) => [c.id, c.name]));
  const cities = (ids: string[]) => (ids.includes(ALL_CITIES) ? "Todas" : ids.map((c) => cityName.get(c) ?? c).join(", "));
  const active = t.members.filter((m) => m.status === "active");
  const shown = t.members.filter((m) => !q || `${m.name} ${m.email}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <PageHead icon="key" title="Equipe e permissões">
        <label className="search" style={{ flexBasis: 240 }}>
          <Icon name="search" size={16} stroke={2} />
          <span className="sr-only">Buscar membro</span>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar membro" />
        </label>
        <Link to="/equipe/auditoria" search={{}} className="btn" style={{ textDecoration: "none" }}><Icon name="shield" size={16} />Auditoria</Link>
        {canEdit && <button type="button" className="btn btn-brasa" onClick={() => setInviting(true)}><Icon name="plus" size={16} stroke={2.4} />Convidar membro</button>}
      </PageHead>

      <section aria-label="Resumo" className="grid-auto" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(170px, 100%), 1fr))" }}>
        <Stat label="Membros ativos" value={fmtInt(active.length)} />
        <Stat label="Funções" value={fmtInt(t.roles.length)} />
        <Stat label="Convites pendentes" value={fmtInt(t.members.filter((m) => m.status === "invited").length)} />
        <Stat label="Com 2FA" value={active.length ? `${Math.round((active.filter((m) => m.twoFactorEnabled).length / active.length) * 100)}%` : "—"} />
      </section>

      <section className="card" style={{ overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 8, padding: "16px 18px 10px" }}>
          <h2 className="card-title">Membros</h2>
          <span className="muted" style={{ fontSize: 13 }}>Login com Google Workspace (@{t.domain}) · 2FA obrigatório</span>
        </div>
        <div className="table-wrap">
          <table className="table" style={{ minWidth: 860 }}>
            <thead><tr><th>Membro</th><th>Função</th><th>Cidades</th><th>Último acesso</th><th>Status</th><th>2FA</th>{canEdit && <th><span className="sr-only">Ações</span></th>}</tr></thead>
            <tbody>
              {shown.map((m) => {
                const [label, tone] = STATUS[m.status];
                const self = m.userId === me.user.id;
                return (
                  <tr key={m.userId}>
                    <td>
                      <span className="cell-person">
                        <span className={`avatar${m.status === "active" ? "" : " avatar-light"}`}>{m.status === "invited" ? "—" : m.initials}</span>
                        <span style={{ display: "flex", flexDirection: "column", minWidth: 0 }}><b>{m.name}{self ? " (você)" : ""}</b><span className="muted ellipsis" style={{ fontSize: 12 }}>{m.status === "invited" ? `${m.email} · convidado ${ago(m.invitedAt)}` : m.email}</span></span>
                      </span>
                    </td>
                    <td><span className="tag" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><span className="tick" style={{ height: 12, background: ROLE_COLOR[m.roleId] ?? "#8C8278" }} />{roleName.get(m.roleId) ?? m.roleId}</span></td>
                    <td style={{ color: "var(--text-2)" }}>{cities(m.cityIds)}</td>
                    <td style={{ color: "var(--text-2)" }}>{m.lastSeenAt ? ago(m.lastSeenAt) : "—"}</td>
                    <td><span className={`pill ${tone}`}>{label}</span></td>
                    <td style={{ fontSize: 13, fontWeight: 600, color: m.twoFactorEnabled ? "var(--ok)" : "var(--muted-2)" }}>{m.twoFactorEnabled ? "Ativo" : "—"}</td>
                    {canEdit && (
                      <td className="right" style={{ whiteSpace: "nowrap" }}>
                        {!self && (
                          <>
                            <button type="button" className="btn btn-ghost" onClick={() => setEditing(m)}>Editar</button>
                            <button type="button" className="btn btn-ghost" style={{ color: m.status === "inactive" ? undefined : "var(--bad)" }} disabled={toggle.isPending} onClick={() => toggle.mutate(m)}>
                              {m.status === "inactive" ? "Reativar" : "Desativar"}
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <ErrorBox error={toggle.error} />
      </section>

      <div className="row">
        <RolesSection team={t} onDone={(msg) => toast.show(msg)} />

        <section className="card pad stack" style={{ flex: "2 1 320px", minWidth: 0, gap: 4 }}>
          <h2 className="card-title" style={{ marginBottom: 6 }}>Registro de auditoria</h2>
          {audit.isPending && <Skeleton h={200} />}
          {audit.data?.items.map((a) => (
            <div key={a.id} style={{ display: "flex", gap: 12, padding: "10px 0" }}>
              <span className="avatar avatar-light" style={{ width: 30, height: 30, fontSize: 11 }}>{a.actorInitials ?? "—"}</span>
              <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 13, lineHeight: "18px" }}><b>{a.actorName ?? "Sistema"}</b> {auditText(a, roleName)}</span>
                <span className="muted" style={{ fontSize: 12 }}>{fmtDateTime(a.createdAt)}</span>
              </span>
            </div>
          ))}
          <Link to="/equipe/auditoria" search={{}} style={{ fontSize: 13, fontWeight: 600, paddingTop: 6 }}>Ver auditoria completa</Link>
        </section>
      </div>

      {inviting && <MemberDialog team={t} onClose={() => setInviting(false)} onDone={(msg) => { setInviting(false); toast.show(msg); }} />}
      {editing && <MemberDialog team={t} member={editing} onClose={() => setEditing(null)} onDone={(msg) => { setEditing(null); toast.show(msg); }} />}
      {toast.node}
    </>
  );
}

function MemberDialog({ team, member, onClose, onDone }: { team: Team; member?: Member; onClose: () => void; onDone: (msg: string) => void }) {
  const me = useSession();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState(member?.roleId ?? "suporte");
  const [all, setAll] = useState(member?.cityIds.includes(ALL_CITIES) ?? false);
  const [cityIds, setCityIds] = useState<string[]>(member?.cityIds.filter((c) => c !== ALL_CITIES) ?? []);
  const scope = all ? [ALL_CITIES] : cityIds;
  const m = useMutation({
    mutationFn: async () => {
      if (member) await call(api.admin.team({ userId: member.userId }).patch({ roleId, cityIds: scope }));
      else await call(api.admin.team.invite.post({ email: email.includes("@") ? email : `${email}@${team.domain}`, name: name || undefined, roleId, cityIds: scope }));
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["team"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
      onDone(member ? "Acesso atualizado" : "Convite enviado");
    },
  });
  return (
    <Dialog title={member ? `Editar ${member.name}` : "Convidar membro"} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); m.mutate(); }}>
        {!member && (
          <>
            <label className="field">E-mail do Google Workspace
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input className="input" required value={email} onChange={(e) => setEmail(e.target.value.trim())} placeholder="nome" autoComplete="off" />
                {!email.includes("@") && <span className="muted" style={{ fontWeight: 500 }}>@{team.domain}</span>}
              </span>
            </label>
            <label className="field">Nome (opcional)
              <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
          </>
        )}
        <label className="field">Função
          <select className="select" value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            {team.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </label>
        <fieldset className="stack" style={{ border: 0, padding: 0, margin: 0, gap: 6 }}>
          <legend className="field" style={{ marginBottom: 6 }}>Cidades</legend>
          {me.allCities && (
            <label className="check"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />Todas as cidades (inclusive futuras)</label>
          )}
          {!all && team.cities.filter((c) => me.allCities || me.cities.some((x) => x.id === c.id)).map((c) => (
            <label key={c.id} className="check">
              <input type="checkbox" checked={cityIds.includes(c.id)} onChange={(e) => setCityIds(e.target.checked ? [...cityIds, c.id] : cityIds.filter((x) => x !== c.id))} />
              {c.name} — {c.state}
            </label>
          ))}
        </fieldset>
        {!member && <p className="notice" style={{ margin: 0 }}>A pessoa entra com o Google da empresa e configura o app autenticador no primeiro acesso.</p>}
        <ErrorBox error={m.error} />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" className="btn" style={{ background: "var(--soft)" }} onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn btn-primary" disabled={m.isPending || !scope.length || (!member && !email)}>{member ? "Salvar" : "Enviar convite"}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ── Funções (role editor) ────────────────────────────────────────────────────────────────────

type Role = Team["roles"][number];
type Perms = Partial<Record<StaffModule, StaffAction[]>>;
const NOT_HELD = "Você não tem essa permissão";

/**
 * Funções: every role with its members and PII access. With `equipe:aprovar` a role is edited as a
 * modules × actions matrix, but only within the editor's own permissions (the API enforces the same).
 */
function RolesSection({ team, onDone }: { team: Team; onDone: (msg: string) => void }) {
  const canApprove = useCan("equipe", "aprovar");
  const me = useSession();
  const [sel, setSel] = useState<string | null>(null);
  const selected = sel === "new" ? null : (team.roles.find((r) => r.id === sel) ?? team.roles[0] ?? null);
  return (
    <section className="card pad stack" style={{ flex: "3 1 560px", minWidth: 0, gap: 14 }}>
      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
        <h2 className="card-title">Funções e permissões</h2>
        {canApprove && <button type="button" className="btn" style={{ background: "var(--soft)" }} disabled={sel === "new"} onClick={() => setSel("new")}><Icon name="plus" size={16} stroke={2.4} />Nova função</button>}
      </div>
      <div className="table-wrap" style={{ margin: "0 -18px" }}>
        <table className="table" style={{ minWidth: 440 }}>
          <thead><tr><th>Função</th><th className="right">Membros</th><th>Dados pessoais</th></tr></thead>
          <tbody>
            {team.roles.map((r) => (
              <tr key={r.id} className="clickable" tabIndex={0} aria-selected={sel !== "new" && r.id === selected?.id} onClick={() => setSel(r.id)} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setSel(r.id))}>
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span className="tick" style={{ height: 12, background: ROLE_COLOR[r.id] ?? "#8C8278" }} />
                    <b style={{ fontWeight: 600 }}>{r.name}</b>
                    {r.id === me.role.id && <span className="tag">Sua função</span>}
                  </span>
                </td>
                <td className="right num">{fmtInt(r.members)}</td>
                <td><span className={`pill ${r.canViewFullPii ? "tone-ok" : "tone-neutral"}`}>{r.canViewFullPii ? "Completos" : "Mascarados"}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel === "new" ? (
        <RoleEditor key="new" onCancel={() => setSel(null)} onDone={(msg, id) => { setSel(id ?? null); onDone(msg); }} />
      ) : selected ? (
        <RoleEditor key={selected.id} role={selected} onDone={(msg, id) => { setSel(id ?? null); onDone(msg); }} />
      ) : null}
    </section>
  );
}

function RoleEditor({ role, onCancel, onDone }: { role?: Role; onCancel?: () => void; onDone: (msg: string, selectId?: string) => void }) {
  const me = useSession();
  const qc = useQueryClient();
  const canApprove = useCan("equipe", "aprovar");
  const mine = me.role.permissions as Perms;
  const holds = (mod: StaffModule, a: StaffAction) => mine[mod]?.includes(a) ?? false;
  const start = useMemo(() => ({ name: role?.name ?? "", perms: (role?.permissions ?? {}) as Perms, pii: role?.canViewFullPii ?? false }), [role]);
  const [name, setName] = useState(start.name);
  const [perms, setPerms] = useState<Perms>(start.perms);
  const [pii, setPii] = useState(start.pii);
  const [deleting, setDeleting] = useState(false);

  // Why the role can't be changed here, if it can't (the API answers the same with 403).
  const beyondMe = !!role && (STAFF_MODULES.some((m) => (role.permissions[m] ?? []).some((a) => !holds(m, a))) || (role.canViewFullPii && !me.role.canViewFullPii));
  const readOnly = !canApprove ? "Só quem tem Equipe · Aprovar muda funções." : role?.id === me.role.id ? "Sua função: outra pessoa com acesso de aprovação precisa alterá-la." : beyondMe ? "Esta função tem permissões que você não tem, então só pode ser vista." : null;

  const on = (mod: StaffModule, a: StaffAction) => perms[mod]?.includes(a) ?? false;
  // Ver comes with any other action in the module; removing Ver removes the rest.
  const toggle = (mod: StaffModule, a: StaffAction) => {
    const cur = new Set(perms[mod] ?? []);
    if (cur.has(a)) {
      cur.delete(a);
      if (a === "ver") cur.clear();
    } else {
      cur.add(a);
      if (a !== "ver" && holds(mod, "ver")) cur.add("ver");
    }
    setPerms({ ...perms, [mod]: STAFF_ACTIONS.filter((x) => cur.has(x)) });
  };
  const clean = Object.fromEntries(STAFF_MODULES.map((m) => [m, perms[m] ?? []]).filter(([, a]) => a!.length)) as Perms;
  const total = Object.values(clean).reduce((n, a) => n + a!.length, 0);
  const dirty = name.trim() !== start.name || pii !== start.pii || JSON.stringify(clean) !== JSON.stringify(Object.fromEntries(STAFF_MODULES.map((m) => [m, start.perms[m] ?? []]).filter(([, a]) => a!.length)));
  const valid = name.trim().length >= 2 && total > 0;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["team"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  };
  const save = useMutation({
    mutationFn: async () => {
      const body = { name: name.trim(), permissions: clean as Record<string, string[]>, canViewFullPii: pii };
      return role ? call(api.admin.team.roles({ id: role.id }).patch(body)) : call(api.admin.team.roles.post(body));
    },
    onSuccess: (r) => {
      refresh();
      onDone(role ? "Função atualizada. Vale no próximo acesso dos membros." : "Função criada.", r.id);
    },
  });

  return (
    <div className="stack" style={{ gap: 12, paddingTop: 4, borderTop: "1px solid var(--soft)" }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 10 }}>
        {readOnly ? (
          <b style={{ flex: 1, fontSize: 16 }}>{role?.name}</b>
        ) : (
          <label className="field" style={{ flex: "1 1 240px" }}>{role ? "Nome da função" : "Nome da nova função"}
            <input className="input" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Atendimento noturno" />
          </label>
        )}
        {role && <span className="muted" style={{ fontSize: 13 }}>{role.members === 1 ? "1 membro" : `${fmtInt(role.members)} membros`}</span>}
      </div>
      {readOnly && <div className="notice">{readOnly}</div>}
      <div className="table-wrap">
        <table className="table" style={{ minWidth: 520 }}>
          <thead><tr><th>Módulo</th>{STAFF_ACTIONS.map((a) => <th key={a} style={{ textAlign: "center" }}>{ACTIONS[a]}</th>)}</tr></thead>
          <tbody>
            {STAFF_MODULES.map((mod) => (
              <tr key={mod}>
                <td><span style={{ display: "flex", flexDirection: "column" }}><b style={{ fontWeight: 600 }}>{MODULES[mod][0]}</b><span className="muted" style={{ fontSize: 12 }}>{MODULES[mod][1]}</span></span></td>
                {STAFF_ACTIONS.map((a) => {
                  const checked = on(mod, a);
                  const held = holds(mod, a);
                  const disabled = !!readOnly || !held;
                  const label = `${ACTIONS[a]} ${MODULES[mod][0]}`;
                  return (
                    <td key={a} style={{ textAlign: "center" }}>
                      {/* The title sits on a wrapper: disabled buttons don't show tooltips everywhere. */}
                      <span title={!readOnly && !held ? NOT_HELD : undefined} style={{ display: "inline-block" }}>
                        <button
                          type="button" role="checkbox" aria-checked={checked} aria-label={!readOnly && !held ? `${label} (${NOT_HELD.toLowerCase()})` : label} disabled={disabled} onClick={() => toggle(mod, a)}
                          style={{ width: 26, height: 26, padding: 0, borderRadius: 8, display: "inline-grid", placeItems: "center", background: checked ? "var(--ink)" : "var(--card)", border: checked ? 0 : "1.5px solid var(--line-2)", color: "var(--panel)", opacity: disabled && !readOnly ? 0.4 : 1, cursor: disabled ? "default" : "pointer" }}
                        >
                          {checked && <Icon name="check" size={13} stroke={3.4} />}
                        </button>
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label className="check" style={{ alignItems: "flex-start", padding: "12px 14px", borderRadius: 14 }} title={!readOnly && !me.role.canViewFullPii ? NOT_HELD : undefined}>
        <input type="checkbox" checked={pii} disabled={!!readOnly || !me.role.canViewFullPii} onChange={(e) => setPii(e.target.checked)} style={{ marginTop: 2 }} />
        <span style={{ flex: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <b>Vê dados pessoais completos</b>
          <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>CPF, telefone e endereço sem máscara · cada acesso fica registrado (LGPD)</span>
        </span>
      </label>
      {!readOnly && <span className="muted" style={{ fontSize: 12 }}>Marcar Editar, Aprovar ou Exportar marca Ver junto. Você só concede o que a sua função tem; as mudanças ficam na auditoria.</span>}
      <ErrorBox error={save.error} />
      {!readOnly && (dirty || !role) && (
        <div className="action-bar">
          <button type="button" className="btn btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>{role ? "Salvar alterações" : "Criar função"}</button>
          {role ? <button type="button" className="btn" onClick={() => { setName(start.name); setPerms(start.perms); setPii(start.pii); }}>Desfazer</button> : <button type="button" className="btn" onClick={onCancel}>Cancelar</button>}
          {!valid && <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>{name.trim().length < 2 ? "Dê um nome à função." : "Marque ao menos uma permissão."}</span>}
        </div>
      )}
      {!readOnly && role && !dirty && (
        <div className="action-bar">
          <span title={role.members ? "Mude a função dos membros antes de excluir" : undefined}>
            <button type="button" className="btn btn-danger" disabled={role.members > 0} onClick={() => setDeleting(true)}><Icon name="ban" size={16} />Excluir função</button>
          </span>
          {role.members > 0 && <span className="muted" style={{ fontSize: 12, alignSelf: "center" }}>Só dá para excluir uma função sem membros.</span>}
        </div>
      )}
      {deleting && role && (
        <ConfirmDialog
          title={`Excluir a função ${role.name}?`}
          body="Ninguém tem esta função agora. A exclusão fica registrada na auditoria."
          confirmLabel="Excluir função"
          danger
          onConfirm={async () => {
            await call(api.admin.team.roles({ id: role.id }).delete());
            refresh();
            onDone("Função excluída.");
          }}
          onClose={() => setDeleting(false)}
        />
      )}
    </div>
  );
}
